/**
 * DeleteFundraiserModal.tsx - Fundraiser Deletion Confirmation Modal
 *
 * This component provides a secure deletion interface for fundraisers with
 * appropriate warnings and confirmation requirements based on fundraiser status.
 *
 * Key Features:
 * - Secure deletion with confirmation requirements
 * - Different confirmation levels based on fundraiser activity
 * - Name confirmation for fundraisers with donations
 * - Warning messages about data loss
 * - Agreement checkbox for fundraisers with payments
 * - Prevention of accidental deletions
 *
 * Deletion Safeguards:
 * - Simple confirmation for fundraisers without donations
 * - Name typing confirmation for active fundraisers
 * - Agreement checkbox for fundraisers with payments
 * - Clear warnings about permanent data loss
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - fundraiser: Fundraiser | null - Fundraiser to delete
 * - onConfirm: () => void - Deletion confirmation handler
 *
 * Dependencies: Fundraiser types, portal rendering, form validation
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "lucide-react";
import { IAPIFundraiser } from "../interfaces";
import { Fundraiser } from "../models/FundraisersGridModels";
import { CreateFundraiserModel } from "../models";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 20px;
`;

const ModalContent = styled.div<{ hasPayments: boolean }>`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: ${(props) => (props.hasPayments ? "550px" : "500px")};
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
  text-align: center;
`;

const FundraiserName = styled.span`
  font-weight: 700;
  color: #dc2626;
`;

const WarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  color: #dc2626;
  font-weight: 600;
  text-align: center;
  line-height: 1.4;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  text-align: center;
`;

const DeleteConfirmInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background-color: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  justify-content: center;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "danger" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #dc2626;
    color: #ffffff;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

interface DeleteFundraiserModalProps {
  isVisible: boolean;
  onClose: () => void;
  fundraiser: CreateFundraiserModel | null;
  onConfirm: () => void;
}

export const DeleteFundraiserModal: React.FC<DeleteFundraiserModalProps> = ({
  isVisible,
  onClose,
  fundraiser,
  onConfirm,
}) => {
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteAgreement, setDeleteAgreement] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Reset form when modal opens
  useEffect(() => {
    if (isVisible) {
      setDeleteConfirmName("");
      setDeleteAgreement(false);
    }
  }, [isVisible]);

  if (!fundraiser) return null;

  const hasPayments = fundraiser.totalRaisedAmount > 0;
  const hasError =
    deleteConfirmName.length > 0 && deleteConfirmName !== fundraiser.name;
  const isDeleteEnabled = hasPayments
    ? deleteConfirmName === fundraiser.name && deleteAgreement
    : true;

  const handleConfirm = () => {
    if (isDeleteEnabled) {
      onConfirm();
    }
  };

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent hasPayments={hasPayments}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Delete Fundraiser</ModalTitle>
        </ModalHeader>

        <ModalText>
          You are deleting fundraiser{" "}
          <FundraiserName>{fundraiser.name}</FundraiserName>. This action cannot
          be undone. Are you sure?
        </ModalText>

        {hasPayments && (
          <>
            <WarningText>
              This fundraiser has received payments. Deleting it will
              permanently remove all donation history and cannot be reactivated.
            </WarningText>

            <FormGroup>
              <FormLabel>
                Type <strong>{fundraiser.name}</strong> below exactly as it
                appears.
              </FormLabel>
              <DeleteConfirmInput
                hasError={hasError}
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Enter fundraiser title"
              />
            </FormGroup>

            <FormGroup>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={deleteAgreement}
                  onChange={(e) => setDeleteAgreement(e.target.checked)}
                />
                I understand that this fundraiser will be deleted and cannot be
                reactivated.
              </CheckboxContainer>
            </FormGroup>
          </>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton
            variant="danger"
            onClick={handleConfirm}
            disabled={!isDeleteEnabled}
          >
            Delete Fundraiser
          </ModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
